import formatDistance from 'date-fns/formatDistance'
import formatISO from 'date-fns/formatISO'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import qs from 'query-string'
import subMonths from 'date-fns/subMonths'
import subWeeks from 'date-fns/subWeeks'
import { differenceInDays, differenceInWeeks } from 'date-fns'

import appText from './appText'

export function translate(path, fallback) {
  return get(appText, path, fallback)
}

export function stringifyQuery(query) {
  if (typeof query === 'string') {
    return query.replace(/^\?/, '')
  }

  return qs.stringify(query, { arrayFormat: 'comma' })
}

export function parseQuery(query) {
  return qs.parse(query, { arrayFormat: 'comma' })
}

export function capitalize(word) {
  if (!word || typeof word !== 'string') {
    return ''
  }

  return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
}

export function titleize(sentence) {
  if (!sentence) {
    return ''
  }
  const stripped = sentence.replace(/_/, ' ')
  let stringArray = stripped.split(' ')
  stringArray = stringArray.map((string) => {
    return capitalize(string)
  })
  return stringArray.join(' ')
}

export function commaFormatted(amount) {
  return (
    Number(amount).toLocaleString()
  )
}

export function formatDistanceDate(date) {
  return formatDistance(new Date(date), new Date(), {
    addSuffix: true
  })
}

export function isNumber(num) {
  return /^-?[\d.]+(?:e-?\d+)?$/.test(num)
}

// accounts for differences in months to years. specifies Today, Yesterday, instead of 1 Days Ago etc
export function formatToWeeksMonths(date) {
  const currentDate = new Date()
  const distanceInDays = differenceInDays(currentDate, new Date(date))
  if (distanceInDays === 0) {
    return 'Today'
  } else if (distanceInDays === 1) {
    return 'Yesterday'
  } else if (distanceInDays < 7) {
    return `${distanceInDays} days ago`
  } else {
    const distanceInWeeks = differenceInWeeks(currentDate, new Date(date))
    if (distanceInWeeks < 4) {
      return `${distanceInWeeks} week${distanceInWeeks > 1 ? 's' : ''} ago`
    } else {
      const distanceInMonths = Math.floor(distanceInWeeks / 4)
      const distanceInYears = Math.floor(distanceInMonths / 12)
      if (distanceInYears > 0) {
        return `${distanceInYears} year${distanceInYears > 1 ? 's' : ''} ago`
      } else {
        return `${distanceInMonths} month${distanceInMonths > 1 ? 's' : ''} ago`
      }
    }
  }
}

export function formatShortDate(dataString) {
  const options = { year: 'numeric', month: 'short', day: 'numeric' }
  return new Date(dataString).toLocaleDateString(undefined, options)
}

export function formatShortDateWithTime(dataString) {
  const options = { year: 'numeric', month: 'short', day: 'numeric' }
  const date = new Date(dataString)
  return date.toLocaleDateString(undefined, options) + ' ' + date.toLocaleTimeString()
}

export function formatMediumDate(dataString) {
  const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }
  return new Date(dataString).toLocaleDateString(undefined, options)
}

export function formatLongDate(dataString) {
  const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
  return new Date(dataString).toLocaleDateString(undefined, options)
}

export const lastWeekRange = {
  start_date: formatISO(subWeeks(new Date(), parseInt(1)), { representation: 'date' }),
  end_date: formatISO(new Date(), { representation: 'date' })
}

export const lastMonthRange = {
  start_date: formatISO(subMonths(new Date(), parseInt(1)), { representation: 'date' }),
  end_date: formatISO(new Date(), { representation: 'date' })
}

export const resolveDate = (value) => {
  if (value === null) {
    return null
  }
  const date = new Date(value)
  return isNaN(date) ? null : date
}

export const formatIsoString = (date) => {
  return new Date(date.split('T')[0].replace(/-/g, '/')).toISOString()
}

export const stripHtml = (html) => {
  if (typeof html !== 'string') {
    return ''
  }
  // NOTE this avoids CSP warnings emitted by DOMParser when parsing inline style rules
  const partialSanitized = html.replace(/\bstyle(\s+)?=/g, 'data-style=')
  const doc = new DOMParser().parseFromString(partialSanitized, 'text/html')
  return doc.body.textContent || ''
}

export function downloadAsFile(data, options = {}) {
  const {
    filename = 'download',
    ...blobOptions
  } = options

  const blob = new Blob([data], blobOptions)

  if (window.navigator && window.navigator.msSaveOrOpenBlob) {
    window.navigator.msSaveOrOpenBlob(blob, filename)
  } else {
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    document.body.appendChild(a)
    a.href = url
    a.download = filename
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }
}

/**
 * Normalizes an object found in the `ioc` data returned for actors & malware
 * into something consistent fields for use in the indicators table and indicators
 * CSV downloads.
 *
 * @param ioc - The `ioc` prop returned from malware & actor endpoints
 */
export function normalizeIoc(ioc) {
  if (isEmpty(ioc)) {
    return null
  }

  const items = Object.entries(ioc).reduce((items, [key, iocs]) => {
    const type = translate(['indicatorSingular', key], titleize(key))

    for (const ioc of iocs) {
      let value, label
      switch (key) {
        case 'domains':
        case 'ips':
        case 'urls':
          value = label = ioc.value
          break
        case 'emails':
          value = label = ioc.label
          break
        case 'files':
          value = label = ioc.label || ioc.name // oddly the "label" here is the md5
          break
        case 'indicators':
          label = ioc.pattern
          break
        default:
      }

      // "label" here is what we want to show to the UI while value is the indicator.
      // Typically they're the same with the exception being emails, which don't expose
      // the indicator (we just have a subject).
      //
      // "value" is the indicator itself and is only useful for IOCs that map to things
      // we can show in nautilus, i.e. *not* emails and not "indicators", which are
      // search patterns.
      //
      // If we can't derive a label, we don't want to return the item.  This has been
      // observed to happen for some files, specifically, which will often have some
      // generic "name" like "unknown", and an object ID, but no hash.
      //
      // The "value" of the ioc is what's used to determine the nautilus UI path, if it
      // exists.
      //
      if (label) {
        items.push({
          id: ioc.id,
          created: ioc.created,
          label,
          type,
          value,
        })
      }
    }

    return items
  }, [])

  items.sort((a, b) => {
    return a.type.localeCompare(b.type) || a.label.localeCompare(b.label)
  })

  return items
}

export const identity = value => value

export const attackPatternCategories = [
  'Reconnaissance',
  'Resource Development',
  'Initial Access',
  'Execution',
  'Persistence',
  'Privilege Escalation',
  'Defense Evasion',
  'Credential Access',
  'Discovery',
  'Lateral Movement',
  'Collection',
  'Command and Control',
  'Exfiltration',
  'Impact',
]

export const stringifyNames = (obj) => {
  if (typeof obj === 'string' && obj.toLowerCase() === 'redacted') {
    return null
  }
  if (typeof obj === 'string') {
    return obj
  }
  if (Array.isArray(obj)) {
    return obj.map(item => item.name).join(', ')
  }
  return null
}

export function toTitleCase(str) {
  const label = str.replaceAll('_', ' ')
  return label?.replace(
    /\w\S*/g,
    function (text) {
      return text.charAt(0).toUpperCase() + text.substr(1).toLowerCase()
    }
  )
}

export const commaSeparator = (num) => {
  return num?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export const csvDateStrFmt = (dateString) => {
  const [date] = dateString.split('T')
  if (date) {
    const dateArr = date.split('-')
    const [year, month, day] = dateArr
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const monthName = monthNames[Number(month) - 1]
    return `${day}-${monthName}-${year}`
  }
}

export const constrainToRange = ({ value, min, max }) => {
  if (value === null || value === undefined) {
    return null
  } else if (value > max) {
    return max
  } else if (value < min) {
    return min
  }
  return value
}

export const transformAliases = (aliases) => {
  if (!aliases) return []

  let withoutAttribution = aliases.map(({ alias }) => alias).filter(Boolean)

  // Malware currently has no alias property so the company attribution
  // needs to be stripped off of the "name". Once the API provides the
  // "alias", this can be removed
  if (!withoutAttribution.length) {
    withoutAttribution = aliases.map(alias => alias.name.split('(')[0].trim())
  }
  // Comma delimited without duplicates
  return [...new Set(withoutAttribution)]
}

export const objectWithoutKey = (object = {}, key = '') => {
  const { [key]: _, ...removed } = object
  return removed
}

export const parseOrCloneDate = (dateInput) => {
  const date = new Date(dateInput)
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date format provided.')
  }
  return date
}
